import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Plus, Trash2, Save, X, Loader2, Search } from "lucide-react";

const FALLBACK_COUNTRIES = ["Deutschland", "Österreich", "Schweiz"];

const EMPTY = {
  company: "", name: "", address: "", zipCode: "", city: "",
  country: "Deutschland", email: "", phone: "", notes: "",
};

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // {id?, ...} or null
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [countryOptions, setCountryOptions] = useState(FALLBACK_COUNTRIES);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/customers"),
        api.get("/settings").catch(() => ({ data: null })),
      ]);
      setCustomers(r.data);
      if (s.data?.countries?.length) {
        setCountryOptions(s.data.countries.map((c) => c.name));
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true); setError("");
    try {
      if (editing.id) {
        await api.put(`/customers/${editing.id}`, editing);
      } else {
        await api.post("/customers", editing);
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Kunden "${c.company || c.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const filtered = customers.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.company || "").toLowerCase().includes(s)
      || (c.name || "").toLowerCase().includes(s)
      || (c.email || "").toLowerCase().includes(s)
      || (c.city || "").toLowerCase().includes(s);
  });

  return (
    <div className="p-4 md:p-10 max-w-5xl" data-testid="customers-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Kundendatenbank</div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-zinc-900">Kunden</h1>
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY })}
          data-testid="new-customer-button"
          className="bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium px-4 py-2.5 rounded-sm flex items-center gap-2"
        >
          <Plus size={14}/> Neuer Kunde
        </button>
      </div>

      {error && (
        <div className="text-xs font-medium text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-sm mb-4">
          {error}
        </div>
      )}

      <div className="mb-4 relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
        <input
          type="text"
          placeholder="Suche nach Firma, Name, E-Mail oder Stadt..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="customer-search"
          className="w-full pl-10 pr-3 py-2 border border-zinc-300 rounded-sm text-sm focus:border-black focus:ring-1 focus:ring-black"
        />
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Wird geladen...</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-zinc-500 py-12 text-center border border-dashed border-zinc-200">
          {customers.length === 0 ? "Noch keine Kunden gespeichert." : "Keine Kunden gefunden."}
        </div>
      ) : (
        <div className="border border-zinc-200">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
            <div className="col-span-3">Firma</div>
            <div className="col-span-2">Ansprechpartner</div>
            <div className="col-span-3">E-Mail / Tel.</div>
            <div className="col-span-3">Adresse</div>
            <div className="col-span-1 text-right">Aktion</div>
          </div>
          {filtered.map((c) => (
            <div key={c.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-zinc-100 last:border-b-0 text-sm hover:bg-zinc-50" data-testid={`customer-row-${c.id}`}>
              <div className="col-span-3 font-medium">{c.company || "—"}</div>
              <div className="col-span-2 text-zinc-600">{c.name || "—"}</div>
              <div className="col-span-3 text-zinc-600 text-xs">
                <div>{c.email || "—"}</div>
                <div>{c.phone || ""}</div>
              </div>
              <div className="col-span-3 text-zinc-600 text-xs">
                {[c.address, [c.zipCode, c.city].filter(Boolean).join(" "), c.country].filter(Boolean).join(", ")}
              </div>
              <div className="col-span-1 flex justify-end gap-1">
                <button onClick={() => setEditing(c)} data-testid={`customer-edit-${c.id}`} className="text-xs text-zinc-600 hover:text-zinc-900 underline">
                  Bearb.
                </button>
                <button onClick={() => remove(c)} data-testid={`customer-delete-${c.id}`} className="text-zinc-400 hover:text-red-600">
                  <Trash2 size={14}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !saving && setEditing(null)}>
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="customer-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
              <h2 className="text-base font-semibold">{editing.id ? "Kunde bearbeiten" : "Neuer Kunde"}</h2>
              <button onClick={() => setEditing(null)} className="text-zinc-500 hover:text-zinc-900"><X size={18}/></button>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Firma" value={editing.company} onChange={(v) => setEditing({ ...editing, company: v })} testId="cust-company"/>
              <Field label="Ansprechpartner" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testId="cust-name"/>
              <Field label="Adresse" value={editing.address} onChange={(v) => setEditing({ ...editing, address: v })} testId="cust-address" full/>
              <Field label="PLZ" value={editing.zipCode} onChange={(v) => setEditing({ ...editing, zipCode: v })} testId="cust-zip"/>
              <Field label="Stadt" value={editing.city} onChange={(v) => setEditing({ ...editing, city: v })} testId="cust-city"/>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">Land</label>
                <select value={editing.country || "Deutschland"} onChange={(e) => setEditing({ ...editing, country: e.target.value })} data-testid="cust-country" className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm focus:border-black focus:ring-1 focus:ring-black">
                  {countryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <Field label="E-Mail" value={editing.email} onChange={(v) => setEditing({ ...editing, email: v })} testId="cust-email"/>
              <Field label="Telefon" value={editing.phone} onChange={(v) => setEditing({ ...editing, phone: v })} testId="cust-phone"/>
              <Field label="Notiz" value={editing.notes} onChange={(v) => setEditing({ ...editing, notes: v })} testId="cust-notes" full multiline/>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-zinc-200">
              <button onClick={() => setEditing(null)} disabled={saving} className="text-sm px-4 py-2 border border-zinc-300 hover:bg-zinc-50">
                Abbrechen
              </button>
              <button onClick={save} disabled={saving} data-testid="customer-save-button" className="bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium px-4 py-2 flex items-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testId, full, multiline }) {
  const cls = "w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm focus:border-black focus:ring-1 focus:ring-black";
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">{label}</label>
      {multiline ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={2} data-testid={testId} className={cls + " min-h-[60px]"}/>
      ) : (
        <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className={cls}/>
      )}
    </div>
  );
}
